/**
 * Authentication context provider.
 *
 * Manages global authentication state for the application. Provides user data,
 * authentication status, and authentication methods (login, register, logout).
 * Automatically verifies token on mount and maintains session state throughout
 * the application lifecycle.
 *
 * @module AuthContext
 */

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { login as loginAPI, loginWithGoogle as loginWithGoogleAPI, register as registerAPI, logout as logoutAPI, verifyToken as verifyTokenAPI } from "@/lib/authApi";
import type { User, LoginResult, RegisterResult } from "@/types/api/auth";
import { logger } from "@/lib/logger";
import { getOrCreateKeyPair } from "@/lib/crypto";
import { setUserPublicKey } from "@/lib/partnerApi";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  googleLogin: (idToken: string) => Promise<LoginResult>;
  register: (
    email: string,
    password: string,
    username: string,
    dataProcessingConsent: boolean,
    voiceRecordingConsent: boolean,
  ) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  isLoading: boolean;
  verifyToken: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function provisionE2EEKeys(userId: string): Promise<void> {
  try {
    const { publicKeyB64 } = await getOrCreateKeyPair(userId);
    await setUserPublicKey(userId, publicKeyB64);
  } catch {
    // Non-critical — messaging still works, just without E2EE for this session
  }
}

export function AuthProvider({ children }: { readonly children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Initialize loading state: always check for existing session on mount
  const [isLoading, setIsLoading] = useState(true);

  const verifyToken = useCallback(async (): Promise<boolean> => {
    // Set loading state at start of verification
    setIsLoading(true);

    try {
      const verifiedUser = await verifyTokenAPI();
      if (verifiedUser) {
        setUser(verifiedUser);
        provisionE2EEKeys(verifiedUser.id);
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      logger.error("Token verification error", error);
      setUser(null);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    // Always verify token on mount (cookies are checked server-side)
    verifyToken().catch(() => {
      // Silent fail - user will be logged out if token is invalid
      logger.debug("Token verification failed on mount");
    });
  }, [verifyToken]);

  useEffect(() => {
    logger.setUserContext(
      user
        ? {
            userId: user.id,
            username: user.username,
            userType: user.userType,
          }
        : null,
    );
  }, [user]);

  const login = useCallback(async (
    email: string,
    password: string,
  ): Promise<LoginResult> => {
    setIsLoading(true);
    try {
      const result = await loginAPI(email, password);
      if (result.success) {
        // Verify token to get user data
        const verifiedUser = await verifyTokenAPI();
        if (verifiedUser) {
          setUser(verifiedUser);
          provisionE2EEKeys(verifiedUser.id);
          logger.event(
            "auth.login.success",
            {
              userId: verifiedUser.id,
              userType: verifiedUser.userType,
            },
            "info",
            "Login succeeded",
          );
        }
      }
      return result;
    } catch (error) {
      logger.error("Login error", error);
      return {
        success: false,
        message: "Unable to connect to server. Please try again later.",
        status: 0,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const googleLogin = useCallback(async (idToken: string): Promise<LoginResult> => {
    setIsLoading(true);
    try {
      const result = await loginWithGoogleAPI(idToken);
      if (result.success) {
        // Verify token to get user data (cookie is set by the backend)
        const verifiedUser = await verifyTokenAPI();
        if (verifiedUser) {
          setUser(verifiedUser);
          provisionE2EEKeys(verifiedUser.id);
          logger.event(
            "auth.login.success",
            {
              userId: verifiedUser.id,
              userType: verifiedUser.userType,
              method: "google",
            },
            "info",
            "Google login succeeded",
          );
        }
      }
      return result;
    } catch (error) {
      logger.error("Google login error", error);
      return {
        success: false,
        message: "Unable to connect to server. Please try again later.",
        status: 0,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    username: string,
    dataProcessingConsent: boolean,
    voiceRecordingConsent: boolean,
  ): Promise<RegisterResult> => {
    setIsLoading(true);
    try {
      const result = await registerAPI(email, password, username, dataProcessingConsent, voiceRecordingConsent);
      if (result.success) {
        // Verify token to get user data
        const verifiedUser = await verifyTokenAPI();
        if (verifiedUser) {
          setUser(verifiedUser);
          provisionE2EEKeys(verifiedUser.id);
          logger.event(
            "auth.register.success",
            {
              userId: verifiedUser.id,
              userType: verifiedUser.userType,
            },
            "info",
            "Registration succeeded",
          );
        }
      }
      return result;
    } catch (error) {
      logger.error("Registration error", error);
      return { success: false, error: "Connection error. Please try again." };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      logger.event("auth.logout.started", undefined, "info", "Logout started");
      await logoutAPI();
    } catch (error) {
      logger.error("Logout error", error);
    } finally {
      setUser(null);
      logger.event("auth.logout.completed", undefined, "info", "Logout completed");
      setIsLoading(false);
    }
  }, []);

  const value: AuthContextType = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      login,
      googleLogin,
      register,
      logout,
      isLoading,
      verifyToken,
    }),
    [user, isLoading, login, googleLogin, register, logout, verifyToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
